import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	RefreshControl,
	ScrollView,
	View,
} from 'react-native';
import { useMutation } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import Toast from 'react-native-root-toast';
import moment from 'moment';

import {
	ROLE_PERMISSIONS,
	STATUS,
	STOCK_DELIVERY_ENDS,
} from '../../../common/constants';
import {
	MARK_STOCK_DELIVERY_ITEMS_DELIVERED,
	UNRECEIVE_STOCK_DELIVERY_ITEMS,
} from '../../../common/graphql/stockDeliveryItem';
import { getStockDeliveryDisplayStatus, isStockDeliveryItemDelivered } from '../../../common/utils/warehousing/stockDeliveryStatus';
import { useUserContext } from '../../../common/utils/context';

import { DetailContainer } from '../../containers';
import { useRefreshPage } from '../../hooks/useRefreshPage';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import {
	Card,
	EmptyState,
	SecondaryButton,
	SectionTitle,
} from '../../design/components';
import Text from '../../components/text/Text';

import { createStockDeliveryDetailStyles } from './StockDeliveryDetail.styles';
import StockDeliveryItemCard from './StockDeliveryItemCard';
import ReceiveWithExpiryDialog from './ReceiveWithExpiryDialog';
import DeliveryBatchesPreviewDialog from './DeliveryBatchesPreviewDialog';
import StockDeliveryScannerDialog from './StockDeliveryScannerDialog';
import useStockDeliveryReceive from './useStockDeliveryReceive';

const HIGHLIGHT_DURATION_MS = 6000;
const SCROLL_TO_ITEM_OFFSET = 180;

const normalizeBarcode = (value) => (value || '').trim();

const toComparableBarcodes = (barcode) => {
	const normalized = normalizeBarcode(barcode);

	if (!normalized || !/^\d+$/.test(normalized)) {
		return normalized ? [normalized] : [];
	}

	if (normalized.length === 12) {
		return [normalized, `0${normalized}`];
	}

	if (normalized.length === 13 && normalized.startsWith('0')) {
		return [normalized, normalized.slice(1)];
	}

	return [normalized];
};

const doesBarcodeMatch = (scannedBarcode, productBarcode) => {
	const scannedVariants = toComparableBarcodes(scannedBarcode);
	const productVariants = toComparableBarcodes(productBarcode);

	if (!scannedVariants.length || !productVariants.length) {
		return false;
	}

	return scannedVariants.some(variant => productVariants.includes(variant));
};

const findDeliveryItemByBarcode = (items, barcode) => {
	const normalizedBarcode = normalizeBarcode(barcode);

	if (!normalizedBarcode) {
		return null;
	}

	return items.find(item => {
		const barcodes = item.product?.barcodes || [];
		return barcodes.some(productBarcode => doesBarcodeMatch(normalizedBarcode, productBarcode));
	});
};

const getDeliveryEndLabel = (endType, endObj) => {
	const endName = endObj?.name || 'Unknown';

	if (endType === STOCK_DELIVERY_ENDS.WAREHOUSE) {
		return `Warehouse: ${endName}`;
	}

	if (endType === STOCK_DELIVERY_ENDS.SUPPLIER) {
		return `Supplier: ${endName}`;
	}

	return endName;
};

const StockDeliveryDetail = ({ data: stockDelivery, refetch }) => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStockDeliveryDetailStyles);
	const { hasPermission, tenant, tenantSettings } = useUserContext();
	const timezone = tenant?.timezone;
	const [refreshPage, isRefreshing] = useRefreshPage(refetch);
	const scrollViewRef = useRef(null);
	const itemLayoutYMapRef = useRef({});

	const [isScannerOpen, setIsScannerOpen] = useState(false);
	const [highlightedItemId, setHighlightedItemId] = useState(null);
	const [pendingScannedItemId, setPendingScannedItemId] = useState(null);
	const [batchPreviewItemId, setBatchPreviewItemId] = useState(null);
	const [markStockDeliveryItemsDeliveredMutation, { loading: isUpdatingDeliveredAt }] = useMutation(MARK_STOCK_DELIVERY_ITEMS_DELIVERED);
	const [unreceiveStockDeliveryItemsMutation, { loading: isUnreceivingItem }] = useMutation(UNRECEIVE_STOCK_DELIVERY_ITEMS);

	const canModifyStockDeliveries = useMemo(() => {
		return Boolean(hasPermission(ROLE_PERMISSIONS.ADD_EDIT_STOCK_DELIVERIES));
	}, [hasPermission]);

	const canViewPurchaseOrderDetail = useMemo(() => {
		return Boolean(hasPermission(ROLE_PERMISSIONS.VIEW_PURCHASE_ORDER_DETAIL_PAGE));
	}, [hasPermission]);

	const deliveryItems = useMemo(() => stockDelivery?.items || [], [stockDelivery?.items]);
	const {
		activeBatchCaptureItem,
		batchCaptureRows,
		setBatchCaptureRows,
		batchCaptureError,
		openReceiveDialog,
		closeReceiveDialog,
		submitReceiveWithExpiry,
		confirmDeliveryQuantityChange,
		isCapturingExpiry,
		isUpdatingQuantity,
	} = useStockDeliveryReceive({
		stockDelivery,
		deliveryItems,
		timezone,
		tenantSettings,
		refetch,
	});
	const activeBatchPreviewItem = useMemo(() => deliveryItems.find(item => item.id === batchPreviewItemId), [batchPreviewItemId, deliveryItems]);
	const hasItems = deliveryItems.length > 0;
	const areAllItemsDelivered = hasItems && deliveryItems.every(item => isStockDeliveryItemDelivered(item));
	const displayStatus = useMemo(() => getStockDeliveryDisplayStatus(stockDelivery), [stockDelivery]);
	const createdLabel = stockDelivery?.created ? moment(stockDelivery.created).format('DD MMM YYYY') : 'Unknown';
	const sourceLabel = getDeliveryEndLabel(stockDelivery?.source, stockDelivery?.sourceObj);
	const destinationLabel = getDeliveryEndLabel(stockDelivery?.destination, stockDelivery?.destinationObj);

	const openPurchaseOrder = useCallback(() => {
		if (canViewPurchaseOrderDetail && stockDelivery?.purchaseOrder?.id) {
			navigation.navigate('PurchaseOrderDetailView', { id: stockDelivery.purchaseOrder.id });
		}
	}, [canViewPurchaseOrderDetail, navigation, stockDelivery?.purchaseOrder?.id]);

	const focusItem = useCallback((itemId) => {
		if (!itemId) {
			return;
		}

		const y = itemLayoutYMapRef.current[itemId];
		if (typeof y === 'number' && scrollViewRef.current?.scrollTo) {
			scrollViewRef.current.scrollTo({
				y: Math.max(y - SCROLL_TO_ITEM_OFFSET, 0),
				animated: true,
			});
		}

		setHighlightedItemId(itemId);
	}, []);

	const toggleDelivered = useCallback(async ({ itemIds, delivered }) => {
		if (!itemIds.length || !canModifyStockDeliveries) {
			return;
		}

		try {
			await markStockDeliveryItemsDeliveredMutation({
				variables: {
					ids: itemIds,
					delivered,
				},
			});
			await refetch();
			Toast.show(delivered ? 'Marked as delivered' : 'Delivered status removed');
		}
		catch (error) {
			Toast.show(error?.message || 'Unable to update delivered status');
		}
	}, [canModifyStockDeliveries, markStockDeliveryItemsDeliveredMutation, refetch]);

	const unreceiveItem = useCallback(async (itemId) => {
		if (!itemId) {
			return;
		}

		try {
			await unreceiveStockDeliveryItemsMutation({
				variables: {
					ids: [itemId],
					mode: 'SAFE',
				},
			});
			await refetch();
			Toast.show('Item unstocked');
		}
		catch (error) {
			Toast.show('Unable to unstock item');
		}
	}, [refetch, unreceiveStockDeliveryItemsMutation]);

	const toggleDeliveredForItem = useCallback(async (item) => {
		if (!item?.id) {
			return;
		}

		const isDelivered = isStockDeliveryItemDelivered(item);
		if (isDelivered && item.receivedAt) {
			Toast.show('Unstock item before undoing delivered status.');
			return;
		}

		await toggleDelivered({
			itemIds: [item.id],
			delivered: !isDelivered,
		});
	}, [toggleDelivered]);

	const handleScanResult = useCallback((barcode) => {
		const matchedItem = findDeliveryItemByBarcode(deliveryItems, barcode);

		if (!matchedItem?.id) {
			Toast.show('Product not found in this delivery');
			return;
		}

		setPendingScannedItemId(matchedItem.id);
		setIsScannerOpen(false);
		if (matchedItem.receivedAt) {
			Toast.show('Product found. It is already stocked.');
		}
		else if (isStockDeliveryItemDelivered(matchedItem)) {
			Toast.show('Product found. It is delivered. Tap Stock item with batches.');
		}
		else {
			Toast.show('Product found. Tap Mark delivered, then Stock item with batches.');
		}
	}, [deliveryItems]);

	const handleToggleAllDelivered = useCallback(async () => {
		if (!hasItems) {
			return;
		}

		if (areAllItemsDelivered && deliveryItems.some(item => item.receivedAt)) {
			Toast.show('Unstock all items before undoing delivered status.');
			return;
		}

		const itemIds = deliveryItems
			.map(item => item.id)
			.filter(Boolean);

		await toggleDelivered({
			itemIds,
			delivered: !areAllItemsDelivered,
		});
	}, [areAllItemsDelivered, deliveryItems, hasItems, toggleDelivered]);

	const handleItemLayout = useCallback((itemId, event) => {
		itemLayoutYMapRef.current[itemId] = event.nativeEvent.layout.y;
	}, []);

	useEffect(() => {
		if (!isScannerOpen && pendingScannedItemId) {
			const timeoutId = setTimeout(() => {
				focusItem(pendingScannedItemId);
				setPendingScannedItemId(null);
			}, 250);

			return () => clearTimeout(timeoutId);
		}
	}, [focusItem, isScannerOpen, pendingScannedItemId]);

	useEffect(() => {
		if (!highlightedItemId) {
			return;
		}

		const timeoutId = setTimeout(() => {
			setHighlightedItemId(null);
		}, HIGHLIGHT_DURATION_MS);

		return () => clearTimeout(timeoutId);
	}, [highlightedItemId]);

	return (
		<View style={styles.container}>
			<ScrollView
				ref={scrollViewRef}
				contentContainerStyle={styles.contentContainer}
				refreshControl={(
					<RefreshControl
						refreshing={isRefreshing}
						onRefresh={refreshPage}
					/>
				)}
			>
				<Text style={styles.heading} accessibilityRole="header">
					Stock delivery {stockDelivery?.identifier || '—'}
				</Text>
				<Card style={styles.summaryCard}>
					<View style={styles.summaryRow}>
						<Text style={styles.label}>Status</Text>
						<Text style={[
							styles.value,
							displayStatus.status === STATUS.COMPLETE && styles.valueComplete,
						]}
						>
							{displayStatus.text}
						</Text>
					</View>
					<View style={styles.summaryRow}>
						<Text style={styles.label}>Created</Text>
						<Text style={styles.value}>{createdLabel}</Text>
					</View>
					<View style={styles.summaryRow}>
						<Text style={styles.label}>Source</Text>
						<Text style={styles.value}>{sourceLabel}</Text>
					</View>
					<View style={styles.summaryRow}>
						<Text style={styles.label}>Destination</Text>
						<Text style={styles.value}>{destinationLabel}</Text>
					</View>
					{stockDelivery?.purchaseOrder?.identifier && (
						<View style={styles.summaryRow}>
							<Text style={styles.label}>Purchase order</Text>
							<Text
								style={[
									styles.value,
									canViewPurchaseOrderDetail && styles.valueLink,
								]}
								onPress={canViewPurchaseOrderDetail ? openPurchaseOrder : undefined}
							>
								{stockDelivery.purchaseOrder.identifier}
							</Text>
						</View>
					)}
				</Card>

				<SectionTitle title="Delivery products" style={styles.sectionTitle} />
				<View style={styles.itemsHeaderActions}>
					<SecondaryButton
						label="Scan product"
						icon="maximize"
						variant="tonal"
						style={styles.itemsHeaderAction}
						onPress={() => setIsScannerOpen(true)}
						disabled={!canModifyStockDeliveries || !hasItems}
					/>
					<SecondaryButton
						label={areAllItemsDelivered ? 'Undo delivered all' : 'Mark delivered all'}
						style={styles.itemsHeaderAction}
						onPress={handleToggleAllDelivered}
						loading={isUpdatingDeliveredAt}
						disabled={!canModifyStockDeliveries || !hasItems}
					/>
				</View>
				<Text style={styles.helperText}>
					Delivered confirms shipment arrival. Stocked confirms warehouse stock update and expiry capture.
				</Text>

				{!canModifyStockDeliveries && (
					<Text style={styles.helperText}>
							You need stock delivery edit permission to scan, mark delivered, and stock items.
					</Text>
				)}

				{!hasItems && (
					<EmptyState title="No products in this delivery." />
				)}

				{deliveryItems.map(item => (
					<StockDeliveryItemCard
						key={item.id}
						item={item}
						isScannedMatch={highlightedItemId && item.id === highlightedItemId}
						timezone={timezone}
						canModifyStockDeliveries={canModifyStockDeliveries}
						isUpdatingDeliveredAt={isUpdatingDeliveredAt}
						isUnreceivingItem={isUnreceivingItem}
						onLayout={handleItemLayout}
						onToggleDelivered={toggleDeliveredForItem}
						onUnstock={unreceiveItem}
						onOpenReceive={openReceiveDialog}
						onViewBatches={setBatchPreviewItemId}
					/>
				))}
			</ScrollView>
			<ReceiveWithExpiryDialog
				item={activeBatchCaptureItem}
				rows={batchCaptureRows}
				setRows={setBatchCaptureRows}
				error={batchCaptureError}
				onClose={closeReceiveDialog}
				onSubmit={submitReceiveWithExpiry}
				onConfirmQuantityChange={confirmDeliveryQuantityChange}
				tenantSettings={tenantSettings}
				timezone={activeBatchCaptureItem?.expiryPolicyContext?.timezone || timezone}
				sourceIsWarehouse={stockDelivery?.source === STOCK_DELIVERY_ENDS.WAREHOUSE}
				loading={isCapturingExpiry || isUpdatingQuantity}
			/>
			<DeliveryBatchesPreviewDialog
				item={activeBatchPreviewItem}
				timezone={activeBatchPreviewItem?.expiryPolicyContext?.timezone || timezone}
				onClose={() => setBatchPreviewItemId(null)}
			/>
			<StockDeliveryScannerDialog
				isOpen={isScannerOpen}
				onClose={() => setIsScannerOpen(false)}
				onBarcodeScanned={handleScanResult}
			/>
		</View>
	);
};

export default DetailContainer(StockDeliveryDetail);
